import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  Box,
  CardHeader,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { IMonitoringJob } from '../../shared/interfaces/IMonitoringJob';
import MonitoringJobItem from '../MonitoringJobItem/MonitoringJobItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarIcon from '@mui/icons-material/EventRepeat';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { Check, Close, InfoOutlined } from '@mui/icons-material';
import Helper from '../../shared/helpers/Helper';
import { Trans, useTranslation } from 'react-i18next';
import GroupService from '../../services/GroupService';
import { IGroup } from '../../shared/interfaces/IGroup';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import SliderStarsAsLabels from '../Sliders/StarsAsLabels/SliderStarsAsLabels';
import { ReactComponent as FlagDe } from '../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../assets/images/flags/nl.svg';
import { constants } from '../../constants/constants';
import { AuthContext } from '../../providers/AuthProvider';
import Confirm from '../Confirm/Confirm';

interface Props {
  id: string;
  companyName: string;
  label: string;
  minDaysPast?: number;
  createdAt: any;
  updatedAt: string;
  jobs: IMonitoringJob[];
  url?: string;
  communitySettings: CommunityManagement;
  platformId: number;
  platformLabel?: string;
  reload: () => Promise<void>;
  defaultEdit?: boolean;
}

const EditMonitoringCard: FC<Props> = ({
  id,
  companyName,
  label,
  createdAt,
  updatedAt,
  url,
  jobs,
  communitySettings,
  reload,
  platformId,
  platformLabel,
  minDaysPast,
  defaultEdit = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  const [edit, setEdit] = useState(defaultEdit);
  const [allGroups, setAllGroups] = useState<IGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<IGroup[]>([]);

  useEffect(() => {
    GroupService.getGroups().then((groups) => {
      setAllGroups(groups);
      setSelectedGroups(groups.filter((g) => g.monitoringEntries.some((e) => String(e.id) === id)));
    });
  }, [id]);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [newLabel, setNewLabel] = useState(label);
  const [newMinDaysPast, setNewMinDaysPast] = useState(minDaysPast);
  const [newUrl] = useState(url);
  const [newCommunitySettings, setNewCommunitySettings] =
    useState<CommunityManagement>(communitySettings);

  const deleteMonitoring = async () => {
    const { success } = await ReviewMonitoringService.deleteMonitoring(id);
    if (!success) return;
    await reload();
  };

  const updateMonitoringData = async () => {
    if (!newUrl) return;
    if (!newCommunitySettings) return;

    const updatedLabel = await ReviewMonitoringService.updateMonitoringData(
      id,
      newLabel,
      newMinDaysPast || 0,
      newCommunitySettings
    );

    await ReviewMonitoringService.updateMonitoringScheduler(id);

    if (updatedLabel === undefined) {
      setNewLabel(label);
      setNewCommunitySettings(communitySettings);
      setEdit(false);
      return;
    }

    // Sync group membership
    for (const group of allGroups) {
      const currentlyIn = group.monitoringEntries.some((e) => String(e.id) === id);
      const shouldBeIn = selectedGroups.some((g) => g.id === group.id);
      if (currentlyIn !== shouldBeIn) {
        const updatedIds = shouldBeIn
          ? [...group.monitoringEntries.map((e) => String(e.id)), id]
          : group.monitoringEntries.filter((e) => String(e.id) !== id).map((e) => String(e.id));
        await GroupService.updateGroup(String(group.id), {
          name: group.name,
          color: group.color,
          monitoringEntryIds: updatedIds,
        });
      }
    }

    await reload();
  };

  const handleScoreThresholdChange = (event: Event, newValue: number | number[]) => {
    setNewCommunitySettings({
      ...newCommunitySettings,
      scoreThreshold: newValue as number,
    });
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewCommunitySettings({
      ...newCommunitySettings,
      useUsername: event.target.checked,
    });
  };

  const handleLimitReplyTo400CharactersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewCommunitySettings({
      ...newCommunitySettings,
      limitReplyTo400Characters: event.target.checked,
    });
  };

  return (
    <Card
      sx={{
        boxShadow: 'unset',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        justifyContent: 'space-between',
        overflow: 'auto',
      }}
    >
      <CardHeader
        sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column-reverse' }}
        title={
          <>
            {!edit ? (
              <CardContent
                sx={{
                  p: theme.spacing(2),
                  maxHeight: 350,
                }}
              >
                <MonitoringJobItem
                  key={url}
                  url={url}
                  label={label}
                  company={companyName}
                  platformId={platformId}
                />
              </CardContent>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  defaultValue={newLabel}
                  label={t('label')}
                  sx={{ mb: theme.spacing(2), mt: theme.spacing(3) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewLabel(event.target.value)
                  }
                />
                {allGroups.length > 0 && (
                  <Autocomplete
                    multiple
                    options={allGroups}
                    getOptionLabel={(option) => option.name}
                    value={selectedGroups}
                    onChange={(_, newValue) => setSelectedGroups(newValue)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option.name}
                          size="small"
                          sx={{ backgroundColor: option.color, color: '#fff' }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t('nav_groups')}
                        placeholder={t('nav_groups')}
                      />
                    )}
                    sx={{ mb: theme.spacing(2) }}
                  />
                )}
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: theme.spacing(2) }}
                >
                  <InputLabel id={t('tonality')}>{t('tonality')}</InputLabel>
                  <Select
                    value={String(newCommunitySettings.tonality.preference)}
                    label={t('tonality')}
                    labelId={t('tonality')}
                    onChange={(event: SelectChangeEvent) =>
                      setNewCommunitySettings({
                        ...newCommunitySettings,
                        tonality: {
                          ...newCommunitySettings.tonality,
                          preference: parseInt(event.target.value),
                        },
                      })
                    }
                  >
                    <MenuItem value={'2'}>{t('tonality_motivating')}</MenuItem>
                    <MenuItem value={'3'}>{t('tonality_constructive')}</MenuItem>
                    <MenuItem value={'1'}>{t('tonality_formal')}</MenuItem>
                    <MenuItem value={'0'}>{t('tonality_personal')}</MenuItem>
                    <MenuItem value={'4'}>{t('tonality_informal')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: theme.spacing(2) }}
                >
                  <InputLabel id={t('pronoun')}>{t('pronoun')}</InputLabel>
                  <Select
                    value={newCommunitySettings.pronoun.preference === 0 ? '0' : '1'}
                    labelId={t('pronoun')}
                    label={t('pronoun')}
                    onChange={(event: SelectChangeEvent) =>
                      setNewCommunitySettings({
                        ...newCommunitySettings,
                        pronoun: {
                          ...newCommunitySettings.pronoun,
                          preference: parseInt(event.target.value),
                        },
                      })
                    }
                  >
                    {constants.communityManagement.pronounOptions.map((pronoun, index) => {
                      return (
                        <MenuItem
                          key={index}
                          value={index}
                        >
                          <Trans t={t}>{pronoun}</Trans>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  value={newCommunitySettings.salutationPrefix}
                  label={t('salutation')}
                  sx={{ mb: theme.spacing(2) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewCommunitySettings({
                      ...newCommunitySettings,
                      salutationPrefix: event.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={newCommunitySettings.greeting}
                  label={t('greeting')}
                  sx={{ mb: theme.spacing(2) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewCommunitySettings({
                      ...newCommunitySettings,
                      greeting: event.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={newCommunitySettings.additionalPrompt}
                  label={t('additional_prompt')}
                  sx={{ mb: theme.spacing(2) }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setNewCommunitySettings({
                      ...newCommunitySettings,
                      additionalPrompt: event.target.value,
                    })
                  }
                />
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={newCommunitySettings.useUsername}
                        onChange={handleUsernameChange}
                      />
                    }
                    label={t('use_username')}
                    sx={{ mb: theme.spacing(2) }}
                  />
                  {platformLabel?.toLowerCase() === 'kununu' && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={newCommunitySettings.limitReplyTo400Characters}
                          onChange={handleLimitReplyTo400CharactersChange}
                        />
                      }
                      label={t('limit_reply_to_400_characters')}
                      sx={{ mb: theme.spacing(2) }}
                    />
                  )}
                </FormGroup>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                    mb: theme.spacing(2),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={newCommunitySettings.languagePreference}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string) => {
                      if (value) {
                        setNewCommunitySettings({
                          ...newCommunitySettings,
                          languagePreference: value as Language,
                        });
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="nl">
                      <FlagNl
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <SliderStarsAsLabels
                  value={newCommunitySettings.scoreThreshold}
                  onChange={handleScoreThresholdChange}
                  sx={{ mt: theme.spacing(1), mb: theme.spacing(3) }}
                />
                {hasRole('can-set-mindayspast') && (
                  <TextField
                    fullWidth
                    size="small"
                    defaultValue={newMinDaysPast}
                    label={t('mindayspast')}
                    sx={{ mb: theme.spacing(2), mt: theme.spacing(3) }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setNewMinDaysPast(parseInt(event.target.value))
                    }
                  />
                )}
              </Box>
            )}
          </>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    {t('created')} {Helper.getFormattedDate(createdAt, false)}
                  </Typography>
                  <Typography variant="body2">
                    {t('refreshed')} {Helper.getFormattedDate(updatedAt, false)}
                  </Typography>
                </Box>
              }
              arrow
            >
              <InfoOutlined
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </Tooltip>
            {!defaultEdit && !edit && (
              <IconButton
                sx={{ mx: theme.spacing(1) }}
                size="small"
                onClick={() => setEdit(true)}
              >
                <EditIcon
                  sx={{ color: theme.palette.primary.main }}
                  fontSize="small"
                />
              </IconButton>
            )}
            {edit && (
              <IconButton
                sx={{ mx: theme.spacing(1) }}
                size="small"
                onClick={() => updateMonitoringData()}
              >
                <Check
                  sx={{ color: theme.palette.primary.main }}
                  fontSize="small"
                />
              </IconButton>
            )}
            {!edit && hasRole('can-service-backdated') && (
              <IconButton
                size="small"
                onClick={async () => {
                  const { success } = await ReviewMonitoringService.serviceBackdated(id);
                  if (!success) return;
                  await reload();
                }}
              >
                <CalendarIcon
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main }}
                />
              </IconButton>
            )}
            {!defaultEdit && !edit && (
              <>
                <IconButton
                  size="small"
                  onClick={() => setDeleteIsOpen(true)}
                >
                  <DeleteIcon
                    color="error"
                    fontSize="small"
                  />
                </IconButton>
                <Confirm
                  text={t('delete_confirm')}
                  open={deleteIsOpen}
                  onCancel={() => setDeleteIsOpen(false)}
                  onOk={() => deleteMonitoring()}
                />
              </>
            )}
            {edit && !defaultEdit && (
              <IconButton
                size="small"
                onClick={() => {
                  setNewLabel(label);
                  setEdit(false);
                }}
              >
                <Close
                  color="error"
                  fontSize="small"
                />
              </IconButton>
            )}
          </Box>
        }
      />
    </Card>
  );
};

export default EditMonitoringCard;
